// Severity handling for DHL Parcel DE Shipping validation results.
//
// DHL grades every shipment separately under `sstatus`. A 2xx means DHL accepted
// the shipment and a printable label exists; it may still attach notes when it
// adjusted something on our behalf — e.g. "The endorsement was changed to the new
// default 'Return to sender'" when `services.endorsement` is omitted. Only a 4xx/5xx
// is an actual rejection with no label.
//
// The old UI collapsed this to `sstatus.status === 200 ? 'success' : 'error'`, so an
// accepted-with-notes shipment rendered red and disabled label creation for the whole
// batch. Grading warnings separately keeps those notes in front of the operator
// without blocking the batch.
//
// Individual messages carry their own `validationState` ('Valid' | 'Warning' | 'Error').
// The server appends synthetic Error entries of its own (the missing-surname check in
// order.controller.js), so an explicit Error always outranks the status code.

export const DHL_ROW_STATUS = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
};

export const getDhlMessages = (row) => row?.validations?.validationMessages ?? [];

export const isDhlErrorMessage = (message) =>
  String(message?.validationState).toLowerCase() === 'error';

const getDhlStatusCode = (row) => {
  const sstatus = row?.validations?.sstatus;
  const code = sstatus?.status ?? sstatus?.statusCode;
  return typeof code === 'number' ? code : undefined;
};

export const getDhlRowStatus = (row) => {
  const messages = getDhlMessages(row);

  if (messages.some(isDhlErrorMessage)) return DHL_ROW_STATUS.ERROR;

  // Message severity decides, and it outranks `sstatus`. DHL stamps every message
  // with its own `validationState`, and a shipment carrying only warnings has been
  // observed alongside a non-2xx `sstatus` (an endorsement-default notice on an
  // otherwise fine order) — so the code is not a reliable rejection signal here.
  // A real rejection always names its cause with an Error-level message, which the
  // check above already catches.
  if (messages.length > 0) return DHL_ROW_STATUS.WARNING;

  // Nothing to read: fall back to the code, then to the server's own verdict.
  const statusCode = getDhlStatusCode(row);
  if (statusCode === undefined) {
    return row?.status === DHL_ROW_STATUS.ERROR ? DHL_ROW_STATUS.ERROR : DHL_ROW_STATUS.SUCCESS;
  }
  return statusCode >= 400 ? DHL_ROW_STATUS.ERROR : DHL_ROW_STATUS.SUCCESS;
};

// Raw DHL verdict for the row, surfaced as a tooltip so the next surprise is visible
// instead of hidden behind a one-word status.
export const getDhlStatusDetail = (row) => {
  const sstatus = row?.validations?.sstatus;
  if (!sstatus) return undefined;
  const code = sstatus.status ?? sstatus.statusCode;
  return [sstatus.title, code !== undefined ? `sstatus ${code}` : null, sstatus.detail]
    .filter(Boolean)
    .join(' · ');
};

// Only a real rejection blocks the batch; warnings are informational.
export const hasBlockingDhlError = (rows) =>
  (rows ?? []).some((row) => getDhlRowStatus(row) === DHL_ROW_STATUS.ERROR);

// Single-shipment re-validation (edit modal). With validate=true DHL answers HTTP 400
// "weak validation error" even when every message is only a Warning — e.g. the
// GoGreen Plus "for this shipment only the service was removed" notice — so the
// server lands in its catch and returns the raw DHL items as `error.data.data`.
// Normalise both paths to the `{ status, validations }` row shape so the caller can
// grade it with getDhlRowStatus instead of treating any non-2xx as a rejection.
export const getDhlValidateResult = (response) => {
  if (response?.data?.data) return response.data.data;
  const item = response?.error?.data?.data?.[0];
  return item ? { status: DHL_ROW_STATUS.ERROR, validations: item } : undefined;
};

// Toast text for a rejected shipment: lead with the Error-level messages so an
// informational warning listed first can't hide the actual cause.
export const getDhlErrorText = (result, orderId) => {
  const messages = getDhlMessages(result);
  const errors = messages.filter(isDhlErrorMessage);
  const shown = errors.length ? errors : messages;
  if (shown.length) {
    return shown.map((m) => `${m?.property || ''}: ${m?.validationMessage || ''}`.trim()).join(' | ');
  }
  return `Order ${orderId} label validation failed!`;
};

export const DHL_ROW_STATUS_CLASS = {
  [DHL_ROW_STATUS.SUCCESS]: 't-text-green-500',
  [DHL_ROW_STATUS.WARNING]: 't-text-amber-500',
  [DHL_ROW_STATUS.ERROR]: 't-text-red-500',
};

// ---------------------------------------------------------------------------
// Packstation (DHL parcel locker) consignees — SHI-627
//
// The server builds one of two consignee shapes:
//   normal:      { name1, contactName, addressStreet, postalCode, city, country, email, ... }
//   Packstation: { name, contactName, lockerID, postNumber, postalCode, city, country, email }
// so the shape itself is the discriminator. lockerID / postNumber may be missing when
// the server could not parse them from the address (it then adds an Error message),
// which is why a `name` without `name1`/`addressStreet` also counts as a locker.

export const LOCKER_ID_PATTERN = /^\d{3}$/;
export const POST_NUMBER_PATTERN = /^\d{6,10}$/;

const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';

export const isPackstation = (consignee) => {
  if (!consignee || typeof consignee !== 'object') return false;
  if ('lockerID' in consignee || 'postNumber' in consignee) return true;
  return 'name' in consignee && !('name1' in consignee) && !('addressStreet' in consignee);
};

// Display values for a Packstation consignee; null marks a missing field.
export const getPackstationInfo = (consignee) => ({
  lockerID: hasValue(consignee?.lockerID) ? String(consignee.lockerID).trim() : null,
  postNumber: hasValue(consignee?.postNumber) ? String(consignee.postNumber).replace(/\s+/g, '') : null,
});

// Client-side checks before re-validating with DHL. Returns { lockerID, postNumber }
// error strings (undefined when fine) — empty object for non-Packstation consignees.
export const getPackstationErrors = (consignee) => {
  if (!isPackstation(consignee)) return {};
  const errors = {};
  const locker = hasValue(consignee.lockerID) ? String(consignee.lockerID).trim() : '';
  const post = hasValue(consignee.postNumber) ? String(consignee.postNumber).replace(/\s+/g, '') : '';
  if (!LOCKER_ID_PATTERN.test(locker)) errors.lockerID = 'Locker number must be exactly 3 digits.';
  if (!POST_NUMBER_PATTERN.test(post)) errors.postNumber = 'Postnummer must be 6–10 digits.';
  return errors;
};

// Clean a consignee before sending it to DHL: drop an empty name2 and, for a
// Packstation, send lockerID as a Number and postNumber as a digits-only string
// (operators paste "917 392 152"). Non-Packstation consignees only lose an empty name2.
export const cleanConsignee = (c) => {
  if (!c) return c;
  const out = { ...c };
  // DHL's locker schema has no name2 (the modal hides it for lockers), so never send one.
  if (out.name2 === '' || isPackstation(out)) delete out.name2;
  if ('postNumber' in out && out.postNumber != null) {
    out.postNumber = String(out.postNumber).replace(/\D/g, '');
  }
  if ('lockerID' in out && out.lockerID !== '' && out.lockerID != null) {
    const digits = String(out.lockerID).replace(/\D/g, '');
    if (digits !== '') out.lockerID = Number(digits);
  }
  return out;
};
